'use client'

import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'
import { useParams, usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'

import Icon from '@/components/ui/icon'
import Popover from '@/components/ui/Popover'
import { useConfigContext } from '@/context/ConfigProvider'
import { cn, extractPathnameLocale, isValidPath, loadImage } from '@/lib/utils'
import { eGeneralInfo } from '@/types/enum'
import { PaymentFooter } from '@/types/home.type'

import FooterInfo from './FooterInfo'

type FooterMobileProps = {
  payments: PaymentFooter[]
}

export default function FooterMobile({ payments }: FooterMobileProps) {
  const { getBasic } = useConfigContext()
  const t = useTranslations('footer')

  const [mounted, setMounted] = useState(false)
  const [openHour, setOpenHour] = useState(false)

  const pathname = usePathname()
  const params = useParams()
  const { slug } = params
  const extractedPathname = extractPathnameLocale(pathname)
  const isSlugPage = /^\/[^/]+$/.test(extractedPathname) && `/${slug}` === extractedPathname // Kiểm tra chỉ có một segment (trang /[slug])

  const details = [
    { title: t('map'), icon: 'tgic icon-map-marker', path: '/lien-he' },
    { title: t('microchip'), icon: 'tgic icon-microchip-solid', path: '/san-pham' },
    { title: t('pcb'), icon: 'tgic icon-th-large-solid', path: '/pcb' },
    { title: t('news'), icon: 'tgic icon-newspaper', path: '/tin-tuc' },
    { title: t('warranty'), icon: 'tgic icon-wrench', path: '/cong-ty/quy-dinh-bao-hanh' },
  ]

  const socials = [
    { icon: 'tgic icon-youtube-square', href: `${getBasic(eGeneralInfo.youtube_page) || '/'}` },
    { icon: 'tgic icon-facebook-square', href: `${getBasic(eGeneralInfo.facebook_page) || '/'}` },
    { icon: 'tgic icon-twitter-x-square', href: `${getBasic(eGeneralInfo.twitter_page) || '/'}` },
    { icon: 'tgic icon-linkedin-square', href: `${getBasic(eGeneralInfo.linkedin_page) || '/'}` },
    { icon: 'tgic icon-instagram-square', href: `${getBasic(eGeneralInfo.instagram_page) || '/'}` },
    { icon: 'tgic icon-tiktok-square', href: `${getBasic(eGeneralInfo.tiktok_page) || '/'}` },
  ]

  useEffect(() => {
    setMounted(true)
  }, [])

  if (pathname === '/gio-hang' || isValidPath(pathname, '/don-hang') || pathname === '/don-hang') {
    return <div className="mb-20"></div>
  }

  return (
    <div
      className={cn({
        'mb-20': mounted && ['/gio-hang', '/thanh-toan'].includes(extractedPathname),
        'mb-[42px]': mounted && isSlugPage,
      })}
    >
      <div className={pathname == '/' ? 'pt-4' : ''}>{pathname == '/' && <FooterInfo />}</div>
      <div className="text-sm leading-[18px]">
        <div className="bg-[#232f3e] text-[#d9d9da]">
          <div className="overflow-x-auto px-4 py-2 no-scrollbar">
            <ul className="flex items-center">
              {details.map((detail, index) => (
                <li key={index} className="flex items-center justify-center border-r last:border-r-0 border-r-[#999] mr-[10px] pr-[10px] py-[3px] whitespace-nowrap w-full">
                  <Icon icon={detail.icon} className="mr-2" />
                  <Link href={detail.path}>{detail.title}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center">
              <Icon icon="tgic icon-phone" />
              <div className="space-y-1 ml-2">
                {mounted && <p dangerouslySetInnerHTML={{ __html: getBasic(eGeneralInfo.mobile)! }}></p>}
                <p>{getBasic(eGeneralInfo.phone_no_icon)}</p>
              </div>
            </div>
            <div className="relative flex items-center justify-start" onClick={() => setOpenHour(!openHour)}>
              <div className="inline-flex items-center gap-1 cursor-pointer">
                <Icon icon="tgic icon-clock" />
                <span>{t('open_hour')}</span>
                <Icon icon="tgic icon-chevron-down" />
              </div>
              <Popover isOpen={openHour} onClose={() => setOpenHour(false)} className="w-[150%] p-1.5 right-0">
                <div className="text-black" dangerouslySetInnerHTML={{ __html: `${getBasic(eGeneralInfo.work_time)}` }} />
              </Popover>
            </div>
          </div>
          <div className="p-4 space-y-1.5">
            <div className="flex items-center space-x-1 !text-sm">
              <div dangerouslySetInnerHTML={{ __html: `${getBasic(eGeneralInfo.company_name)}` }}></div>
            </div>
            <div className="flex items-center space-x-1">
              <div dangerouslySetInnerHTML={{ __html: `${getBasic(eGeneralInfo.address)}` }}></div>
            </div>
            <div className="flex items-center space-x-1">
              <div dangerouslySetInnerHTML={{ __html: `${getBasic(eGeneralInfo.email)}` }}></div>
            </div>
          </div>
        </div>
        <div className="bg-[#131a22] text-[#94a3b8]">
          <div className="pt-4 pb-2 px-2 flex items-center justify-around">
            {socials.map((social, index) => (
              <Link key={index} href={social.href} className="text-white">
                <Icon icon={social.icon} className="text-2xl" />
              </Link>
            ))}
          </div>
          <div className="px-4 py-2 mt-1">
            <p className="mb-1">{t('payment')}</p>
            {payments.map((payment) => (
              <Image
                key={payment.id}
                src={loadImage('medium', payment.image_id)}
                alt={payment.name}
                width={0}
                height={0}
                sizes="100vw"
                className="inline-block rounded mr-2 mt-2 h-8 w-auto max-h-8 max-w-full"
              />
            ))}
          </div>
          <div className="px-4 py-2 mt-2">
            <Link prefetch={false} href={getBasic(eGeneralInfo.online_gov_register) || '/'} target="_blank" className="inline-block hover:opacity-80">
              <Image
                src="/assets/images/footer/tbao.webp"
                alt="Thông báo bộ công thương"
                width={110}
                height={40}
                draggable={false}
                unoptimized
                priority
                className="w-[110px] h-[40px]"
              />
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
